import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  api_get_company_list_url,
  api_create_company_url,
  api_update_company_url,
  api_activate_deactivate_company_url,
  api_get_company_details_url,
  api_get_company_assign_process_url,
  api_get_payment_terms_list_url
} from "./companyApi";
import {
  parser_get_company_list,
  parser_create_company,
  parser_update_company,
  parser_activate_deactivate_company,
  parser_get_company_details,
  parser_get_company_assign_process,
  parser_get_payment_terms_list
} from "./companyParser";

import { PAGE } from "../../helpers/constants";

export const get_company_list = createAsyncThunk(
  "get_company_list",
  async (payload) => {
    try {
      const { page, qs, qn, limit } = payload
      const response = await api_get_company_list_url(page, qs, qn, limit);
      const data = parser_get_company_list(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const get_payment_terms_list = createAsyncThunk(
  "get_payment_terms_list",
  async (payload) => {
    try {
      const response = await api_get_payment_terms_list_url();
      const data = parser_get_payment_terms_list(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const create_company = createAsyncThunk(
  "create_company",
  async (payload) => {
    try {
      const { name, company_logo, company_email, company_domain, payment_type, process_data } = payload;
      const formData = new FormData();
      if (name) formData.append("name", name);
      if (company_domain) formData.append("company_domain", company_domain);
      if (company_email) formData.append("company_email", company_email);
      if (payment_type) formData.append("payment_type", payment_type);
      if (payload.company_logo) {
        formData.append("company_logo", company_logo);
      }
      if (process_data) {
        formData.append("process_data", JSON.stringify(process_data));
      }
      const response = await api_create_company_url(formData);
      const data = parser_create_company(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


export const update_company = createAsyncThunk(
  "update_company",
  async (payload) => {
    try {
      const { company_id, updated_data } = payload
      const formData = new FormData();
      if (updated_data.name) {
        formData.append("name", updated_data.name);
      }
      if (updated_data.company_domain) {
        formData.append("company_domain", updated_data.company_domain);
      }
      if (updated_data.company_email) {
        formData.append("company_email", updated_data.company_email);
      }
      if (updated_data.payment_type) {
        formData.append("payment_type", updated_data.payment_type);
      }
      if (updated_data.company_logo) {
        formData.append("company_logo", updated_data.company_logo);
      }
      if (updated_data.process_data) {
        formData.append("process_data", JSON.stringify(updated_data.process_data));
      }
      const response = await api_update_company_url(company_id, formData);
      const data = parser_update_company(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);



export const activate_deactivate_company = createAsyncThunk(
  "activate_deactivate_company",
  async (payload) => {
    try {
      const { company_id, updated_data } = payload;
      const response = await api_activate_deactivate_company_url(company_id, updated_data);
      const data = parser_activate_deactivate_company(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


export const get_company_details = createAsyncThunk(
  "get_company_details",
  async (payload) => {
    try {
      const response = await api_get_company_details_url(payload);
      const data = parser_get_company_details(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);

export const assign_process_company = createAsyncThunk(
  "assign_process_company",
  async (payload) => {
    try {
      const { company_id, assigned_ids, unassigned_ids } = payload;
      const response = await api_get_company_assign_process_url(company_id, {
        assigned_ids,
        unassigned_ids,
      });
      const data = parser_get_company_assign_process(response);
      return { data };
    } catch (error) {
      throw new Error(error);
    }
  }
);


const initialState = {
  is_company_modal_open: false,
  common_loading: false,
  selected_status: '',
  search_query: '',
  selected_data: null,
  c_action: null,
  company_id: null,
  company_list: [],
  assign_process: [],
  companies: [],
  paymentOptions: [],
  total_company_count: 0,
  active_company: 0,
  active_payment_terms: 0,
  page: PAGE - 1
}

export const companySlice = createSlice({
  name: "company",
  initialState,
  reducers: {
    setCompanyPage: (state, action) => {
      state.page = action.payload;
    },
    setCompanyModalOpen: (state, action) => {
      state.is_company_modal_open = action.payload;
    },
    setAction: (state, action) => {
      state.c_action = action.payload;
    },
    setId: (state, action) => {
      state.company_id = action.payload;
    },
    setStatus: (state, action) => {
      state.selected_status = action.payload ? action.payload : '';
    },
    setSearchQuery: (state, action) => {
      state.search_query = action.payload;
    }
  },
  extraReducers: (builder) => {
    // get company list
    builder.addCase(get_company_list.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_company_list.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_list = action.payload.data.company;
      state.total_company_count = action.payload.data.total_count;
      state.active_company = action.payload.data.company.filter(company => company.is_active === true).length;
    })
    builder.addCase(get_company_list.rejected, (state) => {
      state.common_loading = false;
    })

    // get payment_terms list
    builder.addCase(get_payment_terms_list.pending, (state) => {
      state.common_loading = true;
    });
    builder.addCase(get_payment_terms_list.fulfilled, (state, action) => {
      state.common_loading = false;
      state.paymentOptions = action.payload.data;
    });
    builder.addCase(get_payment_terms_list.rejected, (state) => {
      state.common_loading = false;
    });

    //create company
    builder.addCase(create_company.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(create_company.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_list.push(action.payload.data)
    })
    builder.addCase(create_company.rejected, (state) => {
      state.common_loading = false;
    })
    //update company
    builder.addCase(update_company.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(update_company.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_list = state.company_list.map((company) =>
        company.id === action.payload.data.id ? { ...company, ...action.payload.data } : company
      );
    })
    builder.addCase(update_company.rejected, (state) => {
      state.common_loading = false;
    })
    // activate_deactivate_company
    builder.addCase(activate_deactivate_company.pending, (state) => {
      state.common_loading = true;
    });
    builder.addCase(activate_deactivate_company.fulfilled, (state, action) => {
      state.common_loading = false;
      state.company_list = state.company_list.map((company) =>
        company.id === action.payload.data.id ? { ...company, ...action.payload.data } : company
      );
    });
    builder.addCase(activate_deactivate_company.rejected, (state) => {
      state.common_loading = false;
    })
    //get company details
    builder.addCase(get_company_details.pending, (state) => {
      state.common_loading = true;
    })
    builder.addCase(get_company_details.fulfilled, (state, action) => {
      state.common_loading = false;
      state.selected_data = action.payload.data
    })
    builder.addCase(get_company_details.rejected, (state) => {
      state.common_loading = false;
    })

    // assign_process_company
    builder.addCase(assign_process_company.pending, (state) => {
      state.common_loading = true;
    });
    builder.addCase(assign_process_company.fulfilled, (state, action) => {
      state.common_loading = false;
      state.assign_process = state.assign_process.map((company) =>
        company.id === action.payload.data.id ? { ...company, ...action.payload.data } : company
      );
    });
    builder.addCase(assign_process_company.rejected, (state) => {
      state.common_loading = false;
    })



  }
});

export const {
  setCompanyModalOpen,
  setCompanyPage,
  setAction,
  setId,
  setStatus,
  setSearchQuery
} = companySlice.actions;

export default companySlice.reducer;